import { Organization, PaginatedResponse, UpdateOrganizationData } from '@/types/credits';

import { getStoredToken } from './apiClient';
import { ApiError, authenticatedRequest } from './base.service';

/**
 * Organizations Service
 * Handles all organization-related API endpoints
 */

export const organizationsService = {
  /**
   * Get organizations with pagination
   */
  async getOrganizations(
    page: number = 1,
    limit: number = 10,
    orderDirection: 'ASC' | 'DESC' = 'ASC'
  ): Promise<PaginatedResponse<Organization>> {
    const searchParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      orderDirection,
    });

    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<PaginatedResponse<Organization>>(
      `/api/v1/organizations?${searchParams.toString()}`,
      {},
      token
    );
  },

  /**
   * Get a single organization by ID
   */
  async getOrganization(organizationId: string): Promise<Organization> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Organization>(`/api/v1/organizations/${organizationId}`, {}, token);
  },

  /**
   * Update an organization's editable fields (name, description, status).
   */
  async updateOrganization(
    organizationId: string,
    data: UpdateOrganizationData
  ): Promise<Organization> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    return authenticatedRequest<Organization>(
      `/api/v1/organizations/${organizationId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      token
    );
  },

  /**
   * Permanently delete an organization. Destructive — prefer deactivating
   * (status: 'inactive') via updateOrganization for the standard flow.
   */
  async deleteOrganization(organizationId: string): Promise<void> {
    const token = getStoredToken();
    if (!token) throw new ApiError('No authentication token found', 401);

    await authenticatedRequest<void>(
      `/api/v1/organizations/${organizationId}`,
      { method: 'DELETE' },
      token
    );
  },
};
